import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOnboardPrepLook = 'prep-chip' | 'prep-chip-on' | 'prep-pill' | 'prep-pill-on';
export type WedgeOnboardPrepFieldVariant = 'wedge-prep-time' | 'wedge-prep-time-on';

const PREP_LOOKS = new Set<string>(['prep-chip', 'prep-chip-on', 'prep-pill', 'prep-pill-on']);
export const isWedgeOnboardPrepLook = (look: string): look is WedgeOnboardPrepLook => PREP_LOOKS.has(look);
export const isWedgeOnboardPrepFieldVariant = (variant: unknown): variant is WedgeOnboardPrepFieldVariant => variant === 'wedge-prep-time' || variant === 'wedge-prep-time-on';

/* Released onboarding prep time and subcategory controls (prep-screens-1 OnboardPrepTimePicker and OnboardSubcategories). The ladder chip is a
   40px pill on the surface fill with the separator border; the chosen chip takes the accent border, the accent tint and bold type. The
   subcategory pill is a 44px inline-flex pill on the elevated fill; the chosen pill fills with its own hue (the caller sets --pill-accent) and
   wears a two-layer glow of that hue. Each reads the exact legacy roles, in the order of the source. The caller owns the labels, the handlers,
   the shipped wedge-pill class (hover and focus paint from it), the --pill-accent value and the pop animation. Only a chip keeps the
   transparent 44px target only on a chip: it is 40px tall with an 8px row gap, so the target reaches 2px into the gap and meets no neighbor.
   A pill is 44px tall already, so its target would equal its box, and a browser check showed that its anchor (position and isolation) moves
   the antialiasing of the pill text at rest, beyond a same-side noise control. A pill draws no target, anchor or stacking context. */

const CHIP_MIN = 40, CHIP_PADDING = '9px 14px', PILL_PADDING = '10px 14px', PILL_MIN = 44, PILL_GAP = 5, PILL_RING = 22, PILL_GLOW = 24;
const ROUND = 999, CHIP_FONT = 14, PILL_FONT = 12.5, FIELD_MIN = 40, FIELD_PADDING = '6px 10px', FIELD_RADIUS = 10, FIELD_FONT = 16;

export function wedgeOnboardPrepStyle(look: WedgeOnboardPrepLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'prep-chip' || look === 'prep-chip-on') {
    const on = look === 'prep-chip-on';
    return {
      minHeight: CHIP_MIN, padding: CHIP_PADDING, borderRadius: ROUND, cursor: 'pointer',
      border: `1px solid ${on ? r('accent') : r('separator')}`,
      background: on ? r('accentTint') : r('surface'),
      color: on ? r('accent') : r('label'),
      fontSize: CHIP_FONT, fontWeight: on ? 700 : 500, fontFamily: r('font'),
    };
  }
  const on = look === 'prep-pill-on';
  const hue = 'var(--pill-accent)';
  return {
    padding: PILL_PADDING, borderRadius: ROUND, minHeight: PILL_MIN, boxSizing: 'border-box',
    background: on ? hue : r('bgElev'),
    color: on ? r('onAccent') : r('labelSec'),
    border: on ? `1px solid ${hue}` : `1px solid ${r('cardBorder')}`,
    boxShadow: on ? `0 0 0 3px color-mix(in srgb, ${hue} ${PILL_RING}%, transparent), 0 2px 12px color-mix(in srgb, ${hue} ${PILL_GLOW}%, transparent)` : 'none',
    cursor: 'pointer', fontSize: PILL_FONT, fontWeight: 600, fontFamily: r('font'),
    display: 'inline-flex', alignItems: 'center', gap: PILL_GAP, touchAction: 'manipulation',
  };
}

/* The native time input: a 40px field on the alternate surface, 16px type so iOS Safari does not zoom on focus, and the accent border once the
   typed value is the chosen time. The type, step and label stay the caller's. */
export function wedgeOnboardPrepFieldStyle(variant: WedgeOnboardPrepFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    minHeight: FIELD_MIN, padding: FIELD_PADDING, borderRadius: FIELD_RADIUS, fontSize: FIELD_FONT, fontFamily: 'inherit',
    border: `1px solid ${variant === 'wedge-prep-time-on' ? r('accent') : r('separator')}`,
    background: r('surfaceAlt'), color: r('label'),
  };
}

/* No look draws the transparent target. The pill anchor moved the pill text at device pixel ratio 1 (48 to 1,409 px, 0 px noise), and on a
   chip the target grew the tap region from about 88 by 41 to 88 by 45 px (+19%) against legacy; with the anchor off both hit grids equal legacy. */
export const wedgeOnboardPrepDrawsHit = (look: string): boolean => !isWedgeOnboardPrepLook(look);
